/** A cancellable text-only effect; full speech remains available to assistive tech. */
export function startGuidedTyping(target: Pick<HTMLElement, 'textContent'>, text: string, immediate: boolean) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;
  const letters = Array.from(text);
  let position = 0;
  const cancel = () => { cancelled = true; clearTimeout(timer); };
  const finish = () => { cancel(); target.textContent = text; };
  const tick = () => {
    if (cancelled) return;
    target.textContent = letters.slice(0, ++position).join('');
    if (position < letters.length) timer = setTimeout(tick, /[,.!?]/u.test(letters[position - 1] ?? '') ? 110 : 20);
  };
  target.textContent = '';
  if (immediate) finish();
  else timer = setTimeout(tick, 40);
  return { cancel, finish };
}

export function getGuidedPlanProgress(elapsedMs: number) {
  const t = Math.min(1, Math.max(0, elapsedMs / 4200));
  const percent = (1 - Math.cos(t * Math.PI)) * 50;
  return { percent, completedSteps: percent >= 100 ? 3 : Math.floor(percent / 34) };
}

/** Lay out every word first, then reveal glyphs without changing any line breaks. */
export function startStableGuidedSpeech(target: HTMLElement, text: string, immediate: boolean, onTyping: (typing: boolean) => void) {
  const letters: HTMLElement[] = [];
  const fragment = document.createDocumentFragment();
  for (const token of text.split(/(\s+)/u)) {
    if (/^\s+$/u.test(token)) { fragment.append(document.createTextNode(token)); continue; }
    const word = document.createElement('span');
    word.className = 'guided-speech-word';
    for (const letter of Array.from(token)) {
      const glyph = document.createElement('span');
      glyph.textContent = letter;
      glyph.style.opacity = '0';
      word.append(glyph);
      letters.push(glyph);
    }
    fragment.append(word);
  }
  target.replaceChildren(fragment);
  let previousCount = 0;
  let currentText = '';
  const effect = startGuidedTyping({
    get textContent() { return currentText; },
    set textContent(value) {
      currentText = value ?? '';
      const count = Array.from(currentText.replace(/\s/gu, '')).length;
      for (let i = previousCount; i < count; i++) letters[i]?.style.setProperty('opacity', '1');
      previousCount = count;
      onTyping(!immediate && count < letters.length);
    },
  }, text, immediate);
  return { finish: effect.finish, cancel: () => { effect.cancel(); onTyping(false); } };
}

/** Normalize displayed copy only, preserving canonical answer values in data attributes. */
export function normalizeGuidedDashes(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!node.parentElement?.closest('script, style, textarea')) node.textContent = node.textContent?.replaceAll('—', '-') ?? '';
  }
}
